import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  dayjsTimezone,
  Eventcalendar,
  MbscCalendarEvent,
  MbscCellClickEvent,
  MbscEventUpdateEvent,
} from '@mobiscroll/react';
import EventCalendar from 'core/components/EventCalendar';
import {
  CalendarEvent,
  CalendarViewEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import dayjs, {
  getDefaultTimeZoneId,
} from 'utils/dayjs';
import {
  useFormContext,
} from 'react-hook-form';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import isNil from 'lodash/isNil';
import {
  formatISODateTime,
} from 'utils/date';
import {
  ScheduledEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CalendarView';
import compact from 'lodash/compact';
import {
  convertToCalendarViewEvents,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import clsx from 'clsx';
import {
  getColorByName,
} from 'utils/colors';
import {
  DateTimeIntervalValue,
  fromStartEnd,
  toEndTime,
} from 'core/components/DateTimePicker/dateTimeInterval';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/index';

function getEventDateTime(event: MbscCalendarEvent): DateTimeIntervalValue {
  return fromStartEnd(
    dayjs(event.start?.toString()).tz(),
    dayjs(event.end?.toString()).tz(),
  );
}

type Props = {
  data: CalendarViewEvent[];
  selectedDate: string;
  event?: CalendarEvent;
  isLoading: boolean;
};

export default function EventCalendarSchedule({
  data,
  selectedDate,
  event,
  isLoading,
}: Props) {
  const timezoneId = getDefaultTimeZoneId();
  const eventCalendarRef = useRef<Eventcalendar>(null!);
  const { setValue, getValues, watch } = useFormContext<AppointmentForm>();
  const eventDateTime = watch('eventDateTime');
  const colors = useColorsHashMap();

  function onEventUpdate({ event: updatedEvent }: MbscEventUpdateEvent) {
    setValue('eventDateTime', getEventDateTime(updatedEvent));
  }

  const allEventsRef = useRef<CalendarViewEvent[]>([]);

  useEffect(() => {
    if (!isLoading) {
      allEventsRef.current = data;
    }
  }, [data, isLoading]);

  function updateCurrentEvent(nextValue: DateTimeIntervalValue) {
    const allEvents = allEventsRef.current;
    const currentIndex = allEvents.findIndex((calendarEvent) => (
      calendarEvent.objectId === event?.objectId
    ));
    const [currentEvent] = convertToCalendarViewEvents(
      compact([event]),
      new Set(compact([event?.id])),
      event?.objectId,
    );

    const { serviceFacility } = event ?? {};
    const colorKey = event?.isTimeAvailability
      ? serviceFacility?.nickname
      : event?.typeOfVisitDescription;
    const endTime = toEndTime(nextValue);

    const newCurrentEvent = {
      ...(allEvents?.[currentIndex] ?? {}),
      ...currentEvent,
      title: event?.objectId !== currentEvent?.objectId ? currentEvent?.title ?? '' : `Editing: ${currentEvent?.title ?? ''}`,
      id: `${currentEvent?.id}_reschedule`,
      cssClass: clsx(currentEvent?.cssClass, 'current-event-for-reschedule', {
        'is-provider-availability-slot': event?.isTimeAvailability,
      }),
      color: getColorByName(colors[colorKey ?? '']?.replace('!bg-', '')),
      start: nextValue.startTime.toDate(),
      end: endTime.toDate(),
    };

    const updatedEvents = currentIndex === -1
      ? [...allEvents, newCurrentEvent]
      : [
        ...allEvents.slice(0, currentIndex),
        newCurrentEvent,
        ...allEvents.slice(currentIndex + 1),
      ];

    eventCalendarRef?.current?.setEvents?.(updatedEvents);
    allEventsRef.current = updatedEvents;
  }

  useEffect(() => {
    if (!isLoading && !isNil(eventDateTime?.startTime)) {
      updateCurrentEvent(eventDateTime);
    }
  }, [eventDateTime, data, isLoading]);

  const [
    calendarSelectedDate,
    setCalendarSelectedDate,
  ] = useState<string>(selectedDate);

  useEffect(() => {
    if (dayjs(eventDateTime?.startTime).isValid()) {
      const newDate = formatISODateTime(dayjs(eventDateTime?.startTime));
      if (dayjs(newDate).format('YYYY-MM-DD') !== dayjs(calendarSelectedDate).format('YYYY-MM-DD')) {
        setCalendarSelectedDate(newDate);
      }
    }
  }, [eventDateTime, calendarSelectedDate]);

  return (
    <EventCalendar
      ref={eventCalendarRef as RefObject<Eventcalendar>}
      classes={{
        page: 'calendar-view-day !bg-transparent event-calendar-reschedule',
      }}
      view={{
        schedule: {
          type: 'day',
          startTime: '00:00',
          endTime: '23:59',
          allDay: false,
          currentTimeIndicator: true,
          timeCellStep: 15,
          timeLabelStep: 15,
        },
      }}
      data={data}
      selectedDate={calendarSelectedDate}
      timezonePlugin={dayjsTimezone}
      displayTimezone={timezoneId}
      dataTimezone={timezoneId}
      renderHeader={() => null}
      renderDay={() => null}
      renderScheduleEvent={ScheduledEvent}
      onCellClick={(args?: MbscCellClickEvent) => {
        const { date } = args ?? {};
        if (!isNil(date)) {
          const currentValue = getValues('eventDateTime');
          if (currentValue?.startTime?.isValid()) {
            const nextValue = {
              ...currentValue,
              startTime: dayjs.tz(formatISODateTime(date), timezoneId),
            };

            setValue('eventDateTime', nextValue);
            updateCurrentEvent(nextValue);
          }
        }
      }}
      dragToMove
      dragToResize
      onEventUpdate={onEventUpdate}
    />
  );
}
